import { getNativeEntitlementId } from '@/services/nativeBilling';
import type { SkippablePlatform } from '@/utils/trackingManager';

/**
 * Destination skip lists for a client-side native store conversion. Every
 * sender (useSubscription, useV3Purchase, and the PremiumWelcome fallback)
 * uses these so one native purchase never reaches a destination twice.
 * trial_started reaches GA4 (Firebase) and PostHog. Every other native
 * conversion is PostHog only. RevenueCat's AppsFlyer integration owns native
 * purchase delivery, so AppsFlyer is skipped on both. Google Ads learns about
 * native trials only through that integration (AppsFlyer af_start_trial).
 */
export const NATIVE_TRIAL_STARTED_TRACKING: { skipPlatforms: readonly SkippablePlatform[] } = {
  skipPlatforms: ['meta', 'appsflyer', 'tiktok'],
};
export const NATIVE_PURCHASE_TRACKING: { skipPlatforms: readonly SkippablePlatform[] } = {
  skipPlatforms: ['ga4', 'meta', 'appsflyer', 'google_ads', 'tiktok'],
};

/**
 * While a native trial offer is premium but has no periodType, PremiumWelcome
 * refetches native billing on this cadence, then sends one labelled purchase.
 * The window is short on purpose: a standing poll would also hit members whose
 * snapshot simply has no periodType.
 */
export const NATIVE_TRIAL_PERIOD_POLL_MS = 3_000;
export const NATIVE_TRIAL_PERIOD_WAIT_MS = 30_000;

/** The skip list for one native conversion event. */
export function nativeConversionTracking(
  eventName: 'trial_started' | 'subscription_purchase',
): { skipPlatforms: readonly SkippablePlatform[] } {
  return eventName === 'trial_started' ? NATIVE_TRIAL_STARTED_TRACKING : NATIVE_PURCHASE_TRACKING;
}

/**
 * Offer kinds that start a free trial. RevenueCat can report these as an INTRO
 * period (snapshot status 'active'), so analytics must not rely on the
 * snapshot alone to tell a trial start from a paid purchase. Mirrors the
 * isTrialOffer list in useSubscription's native checkout.
 */
const TRIAL_OFFER_KINDS: ReadonlySet<string> = new Set([
  'annual_trial',
  'monthly_trial',
  'bridge_annual_trial',
  'bridge_monthly_trial',
  'annual_led_annual',
  'annual_monthly_annual_trial',
  'paid_first_rescue_trial',
  'vip_rescue_annual_trial',
]);

export function isTrialOfferKind(offerKind: string | null | undefined): boolean {
  return Boolean(offerKind) && TRIAL_OFFER_KINDS.has(offerKind as string);
}

type EntitlementLike = { periodType?: unknown; isActive?: unknown } | undefined;

/**
 * Raw RevenueCat entitlement periodType (NORMAL / TRIAL / INTRO / PREPAID), for
 * analytics only. Reads the active entitlement; the `all` entry is used only
 * when it is itself active, so an expired prior period is never reported.
 */
export function getEntitlementPeriodType(customerInfo: unknown): string | null {
  const entitlements = (customerInfo as {
    entitlements?: {
      active?: Record<string, EntitlementLike>;
      all?: Record<string, EntitlementLike>;
    };
  } | null)?.entitlements;
  if (!entitlements) return null;
  const id = getNativeEntitlementId();
  const configured = entitlements.all?.[id];
  const entitlement = entitlements.active?.[id] ?? (configured?.isActive === true ? configured : undefined);
  const periodType = entitlement?.periodType;
  return typeof periodType === 'string' && periodType ? periodType : null;
}

/**
 * A trial offer counts as a trial start only with post-purchase evidence:
 * RevenueCat reports the new entitlement as an INTRO or TRIAL period. An
 * ineligible account that StoreKit/Play charged right away gets NORMAL, which
 * stays a paid purchase.
 */
export function isEvidencedTrialPeriod(periodType: string | null): boolean {
  const upper = (periodType || '').toUpperCase();
  return upper === 'INTRO' || upper === 'TRIAL';
}
